import { ArrowRight, BookOpen, Calendar, Sparkles, FileText, MessageSquare, Lightbulb, User, Cpu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mockBlogPosts } from '@/data/mockData';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { LucideIcon } from 'lucide-react';

const categoryLabels: Record<string, string> = {
  'conseils-cv': 'CV & Lettres',
  'preparation-entretiens': 'Entretiens',
  'developpement-competences': 'Compétences',
  'portraits-talents': 'Portraits',
  'innovation-tech': 'Innovation'
};

const categoryConfig: Record<string, { bg: string; text: string; accent: string; Icon: LucideIcon }> = {
  'conseils-cv': { bg: 'bg-emerald-50', text: 'text-emerald-700', accent: 'from-emerald-400 to-teal-500', Icon: FileText },
  'preparation-entretiens': { bg: 'bg-orange-50', text: 'text-orange-700', accent: 'from-orange-400 to-amber-500', Icon: MessageSquare },
  'developpement-competences': { bg: 'bg-blue-50', text: 'text-blue-700', accent: 'from-blue-400 to-indigo-500', Icon: Lightbulb },
  'portraits-talents': { bg: 'bg-purple-50', text: 'text-purple-700', accent: 'from-purple-400 to-pink-500', Icon: User },
  'innovation-tech': { bg: 'bg-cyan-50', text: 'text-cyan-700', accent: 'from-cyan-400 to-blue-500', Icon: Cpu }
};

export function BlogSection() {
  const latestPosts = mockBlogPosts.slice(0, 3);

  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-honor-orange/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      
      <div className="container mx-auto px-6 lg:px-12 relative">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-3 mb-4 px-4 py-2 bg-honor-orange/10 rounded-full">
              <Sparkles className="w-4 h-4 text-honor-orange" />
              <span className="text-honor-orange font-semibold tracking-wide uppercase text-sm">
                Blog & Ressources
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
              Conseils pour <span className="text-honor-orange">votre carrière</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Articles, guides et astuces pour vous aider à décrocher le job de vos rêves
            </p>
          </div>
          <Link to="/blog">
            <Button className="bg-slate-900 hover:bg-slate-800 text-white font-medium shadow-lg hover:shadow-xl transition-all group px-6 py-6">
              Tous les articles
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
        
        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {latestPosts.map((post, index) => {
            const config = categoryConfig[post.category] || categoryConfig['conseils-cv'];
            const CategoryIcon = config.Icon;
            
            return (
              <Link 
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group"
              >
                <article className="h-full flex flex-col bg-white rounded-3xl shadow-sm hover:shadow-2xl transition-all duration-500 overflow-hidden border border-slate-100/80 hover:border-transparent hover:-translate-y-2">
                  {/* Image Header */}
                  <div className="relative h-52 overflow-hidden">
                    {/* Gradient background */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${config.accent} opacity-90`} />
                    
                    {/* Decorative shapes */}
                    <div className="absolute inset-0 overflow-hidden">
                      {/* Large circle */}
                      <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10 group-hover:scale-125 transition-transform duration-700" />
                      {/* Medium circle */}
                      <div className="absolute right-12 bottom-4 w-20 h-20 rounded-full bg-white/10 group-hover:scale-110 transition-transform duration-500" />
                      {/* Small circle */}
                      <div className="absolute left-8 top-1/2 w-12 h-12 rounded-full bg-white/15 group-hover:translate-y-2 transition-transform duration-500" />
                    </div>
                    
                    {/* Center icon decoration */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="relative">
                        <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                          <CategoryIcon className="w-10 h-10 text-white" strokeWidth={1.5} />
                        </div>
                      </div>
                    </div>
                    
                    {/* Category badge */}
                    <div className="absolute top-4 left-4">
                      <span className={`px-4 py-2 ${config.bg} backdrop-blur-sm rounded-full text-xs font-bold ${config.text} shadow-lg flex items-center gap-2`}>
                        <BookOpen className="w-3 h-3" />
                        {categoryLabels[post.category] || post.category}
                      </span>
                    </div>
                    
                    {/* Featured indicator for first post */}
                    {index === 0 && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-xs font-bold text-slate-900 shadow-lg flex items-center gap-1">
                          <Sparkles className="w-3 h-3 text-honor-orange" />
                          À la une
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 flex flex-col p-6 pt-5">
                    <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
                      <Calendar className="w-4 h-4" />
                      <time>
                        {format(post.publishedAt, 'd MMMM yyyy', { locale: fr })}
                      </time>
                    </div>
                    
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-honor-orange transition-colors line-clamp-2 leading-tight">
                      {post.title}
                    </h3>
                    
                    <p className="text-slate-600 line-clamp-2 mb-5 flex-1 leading-relaxed">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className="text-sm font-bold text-honor-orange group-hover:underline inline-flex items-center">
                        Lire l'article
                        <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-2 transition-transform duration-300" />
                      </span>
                      <span className="text-xs text-slate-400 font-medium">
                        {Math.ceil(post.excerpt.length / 100)} min de lecture
                      </span>
                    </div>
                  </div>
                </article>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
